import apiClient from './authService';

class MedicalReportsService {
  // CRUD LAUDOS MÉDICOS
  async listarLaudos(filtros = {}) {
    const params = new URLSearchParams();
    
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.status) params.append('status', filtros.status);
    if (filtros.appointment_id) params.append('appointment_id', filtros.appointment_id);
    if (filtros.patient_id) params.append('patient_id', filtros.patient_id);
    if (filtros.doctor_id) params.append('doctor_id', filtros.doctor_id);
    if (filtros.service_id) params.append('service_id', filtros.service_id);
    if (filtros.date_from) params.append('date_from', filtros.date_from);
    if (filtros.date_to) params.append('date_to', filtros.date_to);
    if (filtros.overdue_only) params.append('overdue_only', filtros.overdue_only);

    // Novos filtros de fluxo de trabalho
    if (filtros.drafted_by_doctor_id) params.append('drafted_by_doctor_id', filtros.drafted_by_doctor_id);
    if (filtros.finalized_by_doctor_id) params.append('finalized_by_doctor_id', filtros.finalized_by_doctor_id);
    if (filtros.reviewed_by_doctor_id) params.append('reviewed_by_doctor_id', filtros.reviewed_by_doctor_id);
    if (filtros.pending_review) params.append('pending_review', filtros.pending_review);

    return apiClient.get(`/api/v1/medical-reports/laudos/?${params.toString()}`);
  }

  async criarLaudo(dadosLaudo) {
    return apiClient.post('/api/v1/medical-reports/laudos/', dadosLaudo);
  }

  async obterLaudo(reportId) {
    return apiClient.get(`/api/v1/medical-reports/laudos/${reportId}/`);
  }

  async atualizarLaudo(reportId, dadosLaudo) {
    return apiClient.put(`/api/v1/medical-reports/laudos/${reportId}/`, dadosLaudo);
  }

  async removerLaudo(reportId) {
    return apiClient.delete(`/api/v1/medical-reports/laudos/${reportId}/`);
  }

  // LAUDOS POR AGENDAMENTO
  async obterLaudoPorAgendamento(appointmentId) {
    return apiClient.get(`/api/v1/medical-reports/agendamentos/${appointmentId}/laudo/`);
  }



  async obterConteudoLaudoPorAgendamento(appointmentId) {
    return apiClient.get(`/api/v1/medical-reports/agendamentos/${appointmentId}/laudo/content/`);
  }

  async obterConteudoLaudo(reportId) {
    return apiClient.get(`/api/v1/medical-reports/laudos/${reportId}/content/`);
  }

  async debugLaudo(reportId) {
    return apiClient.get(`/api/v1/medical-reports/laudos/${reportId}/debug/`);
  }

  async buscarAgendamentoPorAccession(accessionNumber) {
    return apiClient.get(`/api/v1/medical-reports/agendamentos/por-accession/${accessionNumber}/`);
  }

  // NOVO: Buscar laudo diretamente pelo accession_number
  async obterLaudoPorAccession(accessionNumber) {
    return apiClient.get(`/api/v1/medical-reports/laudos/por-accession/${accessionNumber}/`);
  }

  // ================================
  // FLUXO DE TRABALHO MÉDICO
  // ================================

  /**
   * Revisa um laudo médico (apenas para outro médico)
   * @param {number} reportId - ID do laudo
   * @param {string} reviewNotes - Notas da revisão (opcional)
   */
  async revisarLaudo(reportId, reviewNotes = null) {
    return apiClient.post(`/api/v1/medical-reports/laudos/${reportId}/review/`, {
      review_notes: reviewNotes
    });
  }

  /**
   * Adiciona adendos a um laudo finalizado
   * @param {number} reportId - ID do laudo
   * @param {string} addenda - Conteúdo do adendo
   */
  async adicionarAdendo(reportId, addenda) {
    return apiClient.post(`/api/v1/medical-reports/laudos/${reportId}/addenda/`, {
      addenda: addenda
    });
  }

  /**
   * Assina digitalmente um laudo. Esta ação também o finaliza.
   * @param {number} reportId - ID do laudo
   * @param {Object} signatureData - Dados da assinatura (password, signature_notes)
   */
  async assinarLaudo(reportId, signatureData) {
    return apiClient.post(`/api/v1/medical-reports/laudos/${reportId}/sign/`, signatureData);
  }

  // TEMPLATES DE MÁSCARA (apenas para aplicação na interface)
  async listarMascaras() {
    return apiClient.get('/api/v1/masks/list/');
  }

  async obterMascara(mascaraId) {
    return apiClient.get(`/api/v1/masks/get/${mascaraId}/`);
  }

  // ================================
  // EXPORTAÇÃO DE PDF
  // ================================

  /**
   * Gera PDF do laudo médico
   * @param {number} reportId - ID do laudo
   */
  async gerarPDFLaudo(reportId) {
    const response = await apiClient.get(`/api/v1/medical-reports/laudos/${reportId}/pdf/`, {
      responseType: 'blob'
    });
    
    // Criar URL do blob e fazer download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extrair nome do arquivo do header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = `laudo_${reportId}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  }

  /**
   * Gera PDF do laudo médico baseado no agendamento
   * @param {number} appointmentId - ID do agendamento
   */
  async gerarPDFLaudoPorAgendamento(appointmentId) {
    const response = await apiClient.get(`/api/v1/medical-reports/agendamentos/${appointmentId}/laudo/pdf/`, {
      responseType: 'blob'
    });
    
    // Criar URL do blob e fazer download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extrair nome do arquivo do header Content-Disposition
    const contentDisposition = response.headers['content-disposition'];
    let filename = `laudo_agendamento_${appointmentId}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  }

  // UTILITÁRIOS MANTIDOS PARA COMPATIBILIDADE
  async salvarRascunho(reportId, conteudo) {
    return this.atualizarLaudo(reportId, {
      content: conteudo,
      status: 'IN_PROGRESS'
    });
  }

  // ================================
  // HELPERS DE STATUS E PERMISSÕES
  // ================================

  /**
   * Verifica se o usuário pode editar o laudo baseado no workflow
   * @param {Object} laudo - Objeto do laudo com workflow
   */
  podeEditar(laudo) {
    return laudo?.workflow?.can_edit || false;
  }

  /**
   * Verifica se o usuário pode assinar o laudo (e, portanto, finalizá-lo)
   * @param {Object} laudo - Objeto do laudo com workflow
   */
  podeAssinar(laudo) {
    return laudo?.workflow?.can_sign || false;
  }

  /**
   * Verifica se o usuário pode revisar o laudo
   * @param {Object} laudo - Objeto do laudo com workflow
   */
  podeRevisar(laudo) {
    return laudo?.workflow?.can_review || false;
  }

  /**
   * Verifica se o usuário pode adicionar adendos
   * @param {Object} laudo - Objeto do laudo com workflow
   */
  podeAdicionarAdendo(laudo) {
    return laudo?.workflow?.can_add_addenda || false;
  }

  /**
   * Retorna o status formatado do laudo
   * @param {string} status - Status do laudo
   */
  formatarStatus(status) {
    const statusMap = {
      'PENDING': { label: 'Pendente', class: 'text-gray-700', bg: 'bg-gray-200', border: 'border-gray-400', icon: 'Clock' },
      'DRAFT': { label: 'Rascunho', class: 'text-blue-800', bg: 'bg-blue-200', border: 'border-blue-400', icon: 'FileTextIcon' },
      'IN_PROGRESS': { label: 'Em andamento', class: 'text-orange-800', bg: 'bg-orange-200', border: 'border-orange-400', icon: 'FileEdit' },
      'COMPLETED': { label: 'Finalizado', class: 'text-green-800', bg: 'bg-green-200', border: 'border-green-400', icon: 'CheckCircle' },
      'REVIEWED': { label: 'Revisado', class: 'text-purple-800', bg: 'bg-purple-200', border: 'border-purple-400', icon: 'Eye' },
      'DELIVERED': { label: 'Entregue', class: 'text-indigo-800', bg: 'bg-indigo-200', border: 'border-indigo-400', icon: 'Truck' },
      'CANCELLED': { label: 'Cancelado', class: 'text-red-800', bg: 'bg-red-200', border: 'border-red-400', icon: 'XCircle' }
    };
    
    return statusMap[status] || { label: status, class: 'text-gray-700', bg: 'bg-gray-200', border: 'border-gray-400', icon: 'Circle' };
  }

  /**
   * Retorna informações de timeline do laudo
   * @param {Object} laudo - Objeto do laudo com workflow
   */
  obterTimeline(laudo) {
    const timeline = [];
    const workflow = laudo.workflow;

    // Debug: verificar dados de reconvocação
    console.log('LaudoModal: Dados de recall_info:', laudo.recall_info);

    // Verificar se workflow existe antes de acessar suas propriedades
    if (!workflow) {
      console.log('LaudoModal: Workflow não encontrado, retornando timeline vazia');
      return timeline;
    }

    if (workflow.drafted_by && workflow.drafted_at) {
      timeline.push({
        action: 'Iniciado',
        user: workflow.drafted_by.name,
        timestamp: workflow.drafted_at,
        type: 'draft'
      });
    }

    if (workflow.finalized_by && workflow.finalized_at) {
      timeline.push({
        action: 'Finalizado',
        user: workflow.finalized_by.name,
        timestamp: workflow.finalized_at,
        type: 'finalize'
      });
    }

    if (workflow.reviewed_by && workflow.review_date) {
      timeline.push({
        action: 'Revisado',
        user: workflow.reviewed_by.name,
        timestamp: workflow.review_date,
        type: 'review',
        notes: workflow.review_notes
      });
    }

    if (workflow.signed_by && workflow.signed_at) {
      timeline.push({
        action: 'Assinado digitalmente',
        user: workflow.signed_by.name,
        timestamp: workflow.signed_at,
        type: 'sign',
        notes: workflow.signature_notes
      });
    }

    if (workflow.delivered_by && workflow.delivered_at) {
      timeline.push({
        action: 'Entregue',
        user: workflow.delivered_by,
        timestamp: workflow.delivered_at,
        type: 'deliver'
      });
    }

    // === INCLUIR INFORMAÇÕES DE RECONVOCAÇÃO NA TIMELINE ===
    if (laudo.recall_info && laudo.recall_info.is_blocked) {
      const recallInfo = laudo.recall_info;
      
      console.log('LaudoModal: Processando recall_info:', recallInfo);
      
      // Adicionar entrada de bloqueio por reconvocação
      timeline.push({
        action: 'Bloqueado por Reconvocação',
        user: recallInfo.created_by || 'Sistema',
        timestamp: recallInfo.blocked_at || recallInfo.created_at,
        type: 'recall_blocked',
        notes: `Motivo: ${recallInfo.reason || 'Reconvocação solicitada'}\n${recallInfo.description || ''}\nStatus: ${this.getRecallStatusText(recallInfo.status)}`
      });

      // Se foi agendado, adicionar entrada de agendamento
      if (recallInfo.status === 'scheduled' && recallInfo.rescheduled_date) {
        const dataFormatada = this.formatDateBR(recallInfo.rescheduled_date);
        const agendadoPor = recallInfo.scheduled_by || 'Administrativo';
        timeline.push({
          action: 'Reconvocação Agendada',
          user: agendadoPor,
          timestamp: recallInfo.scheduled_at || recallInfo.blocked_at,
          type: 'recall_scheduled',
          notes: `Nova data: ${dataFormatada} às ${recallInfo.rescheduled_time || 'horário não definido'}\nAgendado por: ${agendadoPor}`
        });
      }

      // Se foi concluído, adicionar entrada de conclusão
      if (recallInfo.status === 'completed') {
        let completionNotes = 'Reconvocação concluída - laudo permanece bloqueado';
        
        // Adicionar informação sobre o novo agendamento se disponível
        if (recallInfo.rescheduled_date && recallInfo.rescheduled_time) {
          const dataFormatada = this.formatDateBR(recallInfo.rescheduled_date);
          completionNotes += `\nNovo agendamento realizado em: ${dataFormatada} às ${recallInfo.rescheduled_time}`;
        }
        
        completionNotes += '\nA continuidade será realizada no novo agendamento';
        
        timeline.push({
          action: 'Reconvocação Concluída',
          user: recallInfo.completed_by || 'Sistema',
          timestamp: recallInfo.completed_at || new Date().toISOString(),
          type: 'recall_completed',
          notes: completionNotes
        });
      }

      // Se foi cancelado, adicionar entrada de cancelamento
      if (recallInfo.status === 'cancelled') {
        timeline.push({
          action: 'Reconvocação Cancelada',
          user: recallInfo.cancelled_by || 'Sistema',
          timestamp: recallInfo.cancelled_at || new Date().toISOString(),
          type: 'recall_cancelled',
          notes: 'Laudo desbloqueado - reconvocação cancelada'
        });
      }
    }

    // === INCLUIR ADENDOS NA TIMELINE ===
    if (laudo.addenda) {
      // Cada adendo começa com "ADENDO EM dd/mm/yyyy hh:mm:"
      // Pode haver múltiplos adendos separados por duas quebras de linha
      const adendoRegex = /ADENDO EM (\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):\n([\s\S]*?)(?=(\n\nADENDO EM|$))/g;
      let match;
      while ((match = adendoRegex.exec(laudo.addenda)) !== null) {
        const dia = parseInt(match[1]);
        const mes = parseInt(match[2]) - 1; // Mês começa em 0 no JavaScript
        const ano = parseInt(match[3]);
        const hora = parseInt(match[4]);
        const minuto = parseInt(match[5]);
        const conteudo = match[6].trim();
        
        // Criar objeto Date no formato brasileiro
        const dataAdendo = new Date(ano, mes, dia, hora, minuto);
        
        timeline.push({
          action: 'Adendo',
          user: workflow.signed_by ? workflow.signed_by.name : 'Autor',
          timestamp: dataAdendo,
          type: 'addenda',
          notes: conteudo
        });
      }
    }

    console.log('LaudoModal: Timeline gerada:', timeline);
    return timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }

  /**
   * Retorna o texto do status de reconvocação
   * @param {string} status - Status da reconvocação
   */
  getRecallStatusText(status) {
    const statusMap = {
      'pending': 'Pendente',
      'scheduled': 'Agendado',
      'completed': 'Concluído',
      'cancelled': 'Cancelado'
    };
    return statusMap[status] || status;
  }

  /**
   * Formata data no formato brasileiro
   * @param {string} dateString - Data no formato YYYY-MM-DD
   * @returns {string} Data formatada no formato DD/MM/YYYY
   */
  formatDateBR(dateString) {
    if (!dateString) return '';
    
    try {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString; // Retorna original se não conseguir formatar
    }
  }

  // Mapear status do sistema para status da API
  mapearStatusParaAPI(statusSistema) {
    const mapeamento = {
      'pendente': 'PENDING',
      'rascunho': 'DRAFT',
      'em_andamento': 'IN_PROGRESS',
      'finalizado': 'COMPLETED',
      'revisao': 'REVIEWED',
      'entregue': 'DELIVERED',
      'cancelado': 'CANCELLED'
    };
    return mapeamento[statusSistema] || 'PENDING';
  }

  // Mapear status da API para status do sistema
  mapearStatusDaAPI(statusAPI) {
    const mapeamento = {
      'PENDING': 'pendente',
      'DRAFT': 'rascunho', 
      'IN_PROGRESS': 'em_andamento',
      'COMPLETED': 'finalizado',
      'REVIEWED': 'revisao',
      'DELIVERED': 'entregue',
      'CANCELLED': 'cancelado'
    };
    return mapeamento[statusAPI] || 'pendente';
  }
}

export default new MedicalReportsService(); 